// src/components/QuickPrayerInput.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCountriesForForm } from '../utils/countries';
import SubscriptionModal from './SubscriptionModal';
import WelcomeBackModal from './WelcomeBackModal';
import Toast from './Toast';
import { useUserAuth } from '../contexts/UserAuthContext';
import config from '../config';

// Retraso (ms) antes de abrir los modales informativos tras un envio exitoso.
// Permite que el usuario vea su pedido recien publicado en el feed.
const POST_SUBMIT_MODAL_DELAY_MS = 10000;

const WIZARD_STEPS = [
  { key: 'email', label: 'Tu email', field: 'email' },
  { key: 'nombre', label: 'Tu nombre', field: 'nombre' },
  { key: 'pais', label: 'Tu país', field: 'pais' },
  { key: 'visible', label: 'Visibilidad', field: 'visible' },
];

function QuickPrayerInput({ onSubmit }) {
  const { isAuthenticated, user } = useUserAuth();
  const navigate = useNavigate();
  // Estado de la barra inferior
  const [comentario, setComentario] = useState('');
  const textareaRef = useRef(null);

  // Estado del wizard
  const [showWizard, setShowWizard] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    pais: '',
    visible: true,
  });

  // Estados para auto-fill (misma lógica que NewPrayer.js)
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [autofilledMessage, setAutofilledMessage] = useState('');

  // Estado de envío
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estados para modales post-envío
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [submittedPrayer, setSubmittedPrayer] = useState(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [userStats, setUserStats] = useState({});
  const [subscriptionPreferences, setSubscriptionPreferences] = useState(null);

  // Toast de feedback (errores principalmente)
  const [toast, setToast] = useState(null);

  // Ref al timeout del modal diferido: nos permite cancelar la apertura si
  // el componente se desmonta antes.
  const modalTimeoutRef = useRef(null);

  // Limpieza al desmontar.
  useEffect(() => {
    return () => {
      if (modalTimeoutRef.current) {
        window.clearTimeout(modalTimeoutRef.current);
        modalTimeoutRef.current = null;
      }
    };
  }, []);

  // Ref para auto-focus en cada paso
  const stepInputRef = useRef(null);

  // Auto-focus en el input del paso actual
  useEffect(() => {
    if (showWizard && stepInputRef.current) {
      // Pequeño delay para que la animación del sheet termine
      const timer = setTimeout(() => {
        stepInputRef.current.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showWizard, currentStep]);

  // --- Auto-fill logic (igual que NewPrayer.js) ---
  const getUserDataFromCache = (email) => {
    try {
      const cached = localStorage.getItem('prayer_user_data');
      if (cached) {
        const data = JSON.parse(cached);
        if (data.email === email) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error leyendo cache:', error);
    }
    return null;
  };

  const saveUserDataToCache = (email, nombre, pais) => {
    try {
      const data = { email, nombre, pais, timestamp: Date.now() };
      localStorage.setItem('prayer_user_data', JSON.stringify(data));
    } catch (error) {
      console.error('Error guardando cache:', error);
    }
  };

  const fetchUserDataByEmail = useCallback(async (email) => {
    if (!email || !email.includes('@')) {
      return;
    }

    const cachedData = getUserDataFromCache(email);
    if (cachedData) {
      setFormData((prev) => ({
        ...prev,
        nombre: cachedData.nombre,
        pais: cachedData.pais,
      }));
      setAutofilledMessage(
        `¡Hola ${cachedData.nombre}! Hemos rellenado tus datos.`
      );
      return;
    }

    setIsLoadingUserData(true);
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/api/prayers/user-data/${encodeURIComponent(email)}`
      );
      const data = await response.json();

      if (data.success && data.found) {
        setFormData((prev) => ({
          ...prev,
          nombre: data.data.nombre,
          pais: data.data.pais,
        }));
        setAutofilledMessage(
          `¡Hola ${data.data.nombre}! Hemos rellenado tus datos.`
        );
        saveUserDataToCache(email, data.data.nombre, data.data.pais);
      }
    } catch (error) {
      console.error('Error obteniendo datos del usuario:', error);
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  // Debounce para auto-fill al escribir email
  useEffect(() => {
    if (formData.email === '') {
      setAutofilledMessage('');
    }

    if (formData.email && formData.email.includes('@')) {
      const timer = setTimeout(() => {
        fetchUserDataByEmail(formData.email);
      }, 800);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email, fetchUserDataByEmail]);

  // --- Handlers ---
  const handleComentarioChange = (e) => {
    setComentario(e.target.value);
    // Auto-resize del textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  const handleOpenWizard = () => {
    if (!comentario.trim()) return;

    // Authenticated users skip the wizard entirely
    if (isAuthenticated && user) {
      handleAuthenticatedSubmit();
      return;
    }

    setShowWizard(true);
    setCurrentStep(0);
  };

  // Direct submit for authenticated users
  const handleAuthenticatedSubmit = async () => {
    if (isSubmitting) return;

    // Guardamos el draft antes de limpiar el campo. Si el envio falla,
    // restauramos el texto para que el usuario pueda reintentar.
    const draft = comentario.trim();
    if (!draft) return;

    setIsSubmitting(true);

    // Limpiar el textarea inmediatamente (feedback tipo WhatsApp).
    setComentario('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const fullData = {
      comentario: draft,
      visible: true,
    };

    try {
      const result = await onSubmit(fullData);

      if (result && result.success) {
        const prayerInfo = {
          id: result.data.id,
          email: user.email,
          nombre: user.nombre,
          comentario: fullData.comentario,
        };

        // Diferimos la apertura del modal informativo para que el usuario
        // pueda ver primero su pedido publicado en el feed.
        if (modalTimeoutRef.current) {
          window.clearTimeout(modalTimeoutRef.current);
        }
        modalTimeoutRef.current = window.setTimeout(() => {
          modalTimeoutRef.current = null;
          if (result.hasActiveSubscription) {
            setSubmittedPrayer(prayerInfo);
            setUserStats(result.userStats || {});
            setSubscriptionPreferences(result.subscriptionPreferences || {});
            setShowWelcomeBackModal(true);
          } else {
            setSubmittedPrayer(prayerInfo);
            setIsReturningUser(result.isReturningUser || false);
            setUserStats(result.userStats || {});
            setShowSubscriptionModal(true);
          }
        }, POST_SUBMIT_MODAL_DELAY_MS);
      } else {
        // Respuesta sin `success`: tratamos como error controlado.
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error al enviar pedido:', error);
      // Restauramos el texto para que el usuario reintente con un solo tap.
      setComentario(draft);
      if (textareaRef.current) {
        // Auto-resize tras restaurar el contenido.
        const ta = textareaRef.current;
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
      }
      setToast({
        id: Date.now(),
        type: 'error',
        message: 'No pudimos enviar tu pedido. Toca enviar de nuevo para reintentar.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseWizard = () => {
    setShowWizard(false);
    setCurrentStep(0);
    setFormData({ email: '', nombre: '', pais: '', visible: true });
    setAutofilledMessage('');
  };

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const canAdvance = () => {
    const step = WIZARD_STEPS[currentStep];
    if (step.key === 'email') return formData.email && formData.email.includes('@');
    if (step.key === 'nombre') return formData.nombre.trim() !== '';
    if (step.key === 'pais') return formData.pais !== '';
    if (step.key === 'visible') return true;
    return true;
  };

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    } else {
      handleCloseWizard();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (currentStep < WIZARD_STEPS.length - 1 && canAdvance()) {
        handleNext();
      }
    }
  };

  const handleBarKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleOpenWizard();
    }
  };

  // --- Submit ---
  const handleSubmit = async () => {
    if (isSubmitting) return;

    const draft = comentario.trim();
    if (!draft) return;

    setIsSubmitting(true);

    const fullData = {
      ...formData,
      comentario: draft,
    };

    // Snapshot del wizard/textarea para rollback en caso de error.
    const wizardSnapshot = {
      formData: { ...formData },
      currentStep,
      autofilledMessage,
    };

    // Cerrar wizard y limpiar textarea inmediatamente (feedback rapido).
    setShowWizard(false);
    setComentario('');
    setCurrentStep(0);
    setFormData({ email: '', nombre: '', pais: '', visible: true });
    setAutofilledMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      saveUserDataToCache(fullData.email, fullData.nombre, fullData.pais);
      const result = await onSubmit(fullData);

      if (result && result.success) {
        const prayerInfo = {
          id: result.data.id,
          email: fullData.email,
          nombre: fullData.nombre,
          comentario: fullData.comentario,
        };

        if (modalTimeoutRef.current) {
          window.clearTimeout(modalTimeoutRef.current);
        }
        modalTimeoutRef.current = window.setTimeout(() => {
          modalTimeoutRef.current = null;
          if (result.hasActiveSubscription) {
            setSubmittedPrayer(prayerInfo);
            setUserStats(result.userStats || {});
            setSubscriptionPreferences(result.subscriptionPreferences || {});
            setShowWelcomeBackModal(true);
          } else {
            setSubmittedPrayer(prayerInfo);
            setIsReturningUser(result.isReturningUser || false);
            setUserStats(result.userStats || {});
            setShowSubscriptionModal(true);
          }
        }, POST_SUBMIT_MODAL_DELAY_MS);
      } else {
        throw new Error('Respuesta inesperada del servidor');
      }
    } catch (error) {
      console.error('Error al enviar pedido:', error);
      // Restaurar textarea + formData del wizard (no reabrimos el wizard para
      // no ser intrusivos; el usuario reintenta tocando enviar de nuevo).
      setComentario(draft);
      setFormData(wizardSnapshot.formData);
      setCurrentStep(wizardSnapshot.currentStep);
      setAutofilledMessage(wizardSnapshot.autofilledMessage);
      if (textareaRef.current) {
        const ta = textareaRef.current;
        ta.style.height = 'auto';
        ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
      }
      setToast({
        id: Date.now(),
        type: 'error',
        message: 'No pudimos enviar tu pedido. Toca enviar de nuevo para reintentar.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Modal handlers ---
  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSubmittedPrayer(null);
    setIsReturningUser(false);
    setUserStats({});
  };

  const handleCloseWelcomeBackModal = () => {
    setShowWelcomeBackModal(false);
    setSubmittedPrayer(null);
    setUserStats({});
    setSubscriptionPreferences(null);
  };

  // --- Render de cada paso ---
  const renderStep = () => {
    const step = WIZARD_STEPS[currentStep];

    switch (step.key) {
      case 'email':
        return (
          <div className="wizard-step-content">
            <label className="wizard-label">Tu email</label>
            <input
              ref={stepInputRef}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleFieldChange}
              onKeyDown={handleKeyDown}
              placeholder="tu.email@ejemplo.com"
              className="wizard-input"
              autoComplete="email"
            />
            {isLoadingUserData && (
              <div className="wizard-autofill-loading">
                <span className="load-more-spinner"></span> Buscando tus datos...
              </div>
            )}
            {autofilledMessage && !isLoadingUserData && (
              <div className="wizard-autofill-message">
                {autofilledMessage}
              </div>
            )}
          </div>
        );

      case 'nombre':
        return (
          <div className="wizard-step-content">
            <label className="wizard-label">Tu nombre completo</label>
            <input
              ref={stepInputRef}
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleFieldChange}
              onKeyDown={handleKeyDown}
              placeholder="Tu nombre completo"
              className="wizard-input"
              autoComplete="name"
            />
          </div>
        );

      case 'pais':
        return (
          <div className="wizard-step-content">
            <label className="wizard-label">Tu país</label>
            <select
              ref={stepInputRef}
              name="pais"
              value={formData.pais}
              onChange={handleFieldChange}
              className="wizard-select"
            >
              <option value="">Selecciona tu país</option>
              {getCountriesForForm().map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.name}
                </option>
              ))}
            </select>
          </div>
        );

      case 'visible':
        return (
          <div className="wizard-step-content">
            <label className="wizard-label">Visibilidad del pedido</label>
            <div className="wizard-checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="visible"
                  checked={formData.visible}
                  onChange={handleFieldChange}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className="checkbox-text">
                  Acepto que mi pedido sea visible para la comunidad de oración
                </span>
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <>
      {/* Barra fija inferior estilo WhatsApp */}
      <div className="quick-prayer-bar">
        {!isAuthenticated && (
          <div className="quick-prayer-login-hint">
            <button className="quick-prayer-login-link" onClick={() => navigate('/login')}>
              Inicia sesión
            </button>
            {' '}para enviar pedidos más rápido
          </div>
        )}
        <div className="quick-prayer-bar-inner">
          <textarea
            ref={textareaRef}
            className="quick-prayer-input"
            placeholder={isAuthenticated ? `${user?.nombre?.split(' ')[0] || ''}, escribe tu pedido de oración...` : 'Escribe tu pedido de oración...'}
            value={comentario}
            onChange={handleComentarioChange}
            onKeyDown={handleBarKeyDown}
            rows={1}
            maxLength={500}
          />
          <button
            className={`quick-prayer-send ${comentario.trim() ? 'active' : ''} ${isSubmitting ? 'submitting' : ''}`}
            onClick={handleOpenWizard}
            disabled={!comentario.trim() || isSubmitting}
            aria-label={isAuthenticated ? 'Enviar pedido' : 'Continuar con el pedido'}
          >
            {isSubmitting ? (
              <span className="load-more-spinner"></span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
            )}
          </button>
        </div>
        {comentario.length > 0 && (
          <div className="quick-prayer-char-count">
            {comentario.length}/500
          </div>
        )}
      </div>

      {/* Bottom-sheet wizard */}
      {showWizard && (
        <div className="prayer-wizard-overlay" onClick={handleCloseWizard}>
          <div
            className="prayer-wizard-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle de arrastre visual */}
            <div className="wizard-handle"></div>

            {/* Indicador de progreso */}
            <div className="wizard-progress">
              {WIZARD_STEPS.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-progress-dot ${
                    index === currentStep
                      ? 'active'
                      : index < currentStep
                      ? 'completed'
                      : ''
                  }`}
                />
              ))}
            </div>

            {/* Preview del pedido */}
            <div className="wizard-prayer-preview">
              <span className="wizard-prayer-preview-label">Tu pedido:</span>
              <p className="wizard-prayer-preview-text">"{comentario}"</p>
            </div>

            {/* Paso actual */}
            <div className="wizard-step-container">
              {renderStep()}
            </div>

            {/* Navegación */}
            <div className="wizard-nav">
              <button className="wizard-nav-back" onClick={handleBack}>
                {currentStep === 0 ? 'Cancelar' : 'Atrás'}
              </button>

              {isLastStep ? (
                <button
                  className={`wizard-nav-submit ${isSubmitting ? 'submitting' : ''}`}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="load-more-spinner"></span>
                      Enviando...
                    </>
                  ) : (
                    'Enviar Pedido'
                  )}
                </button>
              ) : (
                <button
                  className="wizard-nav-next"
                  onClick={handleNext}
                  disabled={!canAdvance()}
                >
                  Siguiente
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Suscripción */}
      {showSubscriptionModal && submittedPrayer && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={handleCloseSubscriptionModal}
          prayerData={submittedPrayer}
          isReturningUser={isReturningUser}
          userStats={userStats}
        />
      )}

      {/* Modal de Bienvenida para Usuarios Suscritos */}
      {showWelcomeBackModal && submittedPrayer && (
        <WelcomeBackModal
          isOpen={showWelcomeBackModal}
          onClose={handleCloseWelcomeBackModal}
          prayerData={submittedPrayer}
          userStats={userStats}
          currentPreferences={subscriptionPreferences}
        />
      )}

      {/* Toast de feedback (errores de envio). `key` fuerza remount en cada
          nuevo mensaje para reiniciar la animacion. */}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={6000}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

export default QuickPrayerInput;
